import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { IconBadge } from '@/components/shared/icon-badge'
import { cn } from '@/lib/utils'

interface ErrorCardProps {
  title: string
  description: string
  onRetry?: () => void
  retryLabel?: string
  /** Shows the retry button as pending (spinner, clicks blocked) while a retry is in flight. */
  retryPending?: boolean
  /**
   * 'inline' renders a standalone styled block; 'card' wraps in a shadcn Card; 'bare' is the
   * icon + copy + retry with no frame of its own, for a body that already sits inside a card
   * (`SectionErrorBoundary`'s `header` mode — a card inside a card doubles the border).
   */
  variant?: 'inline' | 'card' | 'bare'
  className?: string
}

function ErrorCardContent({
  title,
  description,
  onRetry,
  retryLabel = 'Try again',
  retryPending,
}: Omit<ErrorCardProps, 'variant' | 'className'>) {
  return (
    <>
      <IconBadge icon={<AlertCircle />} tone="rose" theme="filled" size="md" />
      <div className="text-center space-y-1">
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {onRetry && (
        <Button variant="outline" onClick={onRetry} pending={retryPending} className="gap-1.5">
          <RefreshCw className="size-4" />
          {retryLabel}
        </Button>
      )}
    </>
  )
}

export function ErrorCard({
  title,
  description,
  onRetry,
  retryLabel,
  retryPending,
  variant = 'inline',
  className,
}: ErrorCardProps) {
  if (variant === 'bare') {
    return (
      <div className={cn('flex flex-col items-center gap-3 py-6', className)}>
        <ErrorCardContent
          title={title}
          description={description}
          onRetry={onRetry}
          retryLabel={retryLabel}
          retryPending={retryPending}
        />
      </div>
    )
  }

  if (variant === 'card') {
    return (
      <Card className={className}>
        <CardContent className="py-10 flex flex-col items-center gap-3">
          <ErrorCardContent
            title={title}
            description={description}
            onRetry={onRetry}
            retryLabel={retryLabel}
            retryPending={retryPending}
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-rose-bg p-6 flex flex-col items-center gap-3',
        className
      )}
    >
      <ErrorCardContent
        title={title}
        description={description}
        onRetry={onRetry}
        retryLabel={retryLabel}
        retryPending={retryPending}
      />
    </div>
  )
}
